import React from 'react';
import { StatusProps, StatusTone, StatusVariant } from '../../../types/DesignSystem/Status';
import { AlertTriangle } from '../Iconography/Icons';
import Typography from '../Typography/Typography';
import Tooltip from '../Tooltip/Tooltip';
import { withClassName } from '../cn';

/**
 * Tone → chip classes.
 *
 * **Every pair is measured against its own tint, not against white.** That distinction
 * is the whole reason this map changed on 2026-08-24: `warning` was
 * `yellow-warning-900` on `yellow-warning-100`, which is **4.36:1** — a WCAG AA failure
 * at 12px/500, where 4.5:1 is required. The same ink on *white* is 4.71:1 and passes,
 * which is exactly why nothing caught it: a contrast figure without its ground named
 * is not a contrast figure. `palette.ts`'s own `ROLE` map already said
 * `"yellow-warning-950": "warning TEXT — use this, not 900"`; the component was not
 * following its own palette.
 *
 * Ratios on the tint each sits on, for the next person who changes one:
 *   neutral 5.18 · info 7.85 · accent 9.06 · success 7.54 · warning 6.86 · danger 5.58
 *
 * `info` is `blue-900`, which is also the resting action ink across the eight surfaces
 * counted in § 29. That is acceptable *here* and nowhere near a control: the chip has
 * no hover, no cursor change and no focus ring, so there is no affordance for the
 * colour to falsely advertise. Do not reuse this pair on anything clickable.
 */
/*
 * § 299 — the DOT form's ink: the tone's GLYPH step, the mark a Toast or Alert draws on
 * a plain surface, measured ≥3:1 there in both themes (SC 1.4.11). Painted as `text-*`
 * so the dot and its pulse halo ride `bg-current`, exactly as the chip form's do.
 */
const dotInk: Record<StatusTone, string> = {
  neutral: 'tw-text-tone-neutral-glyph',
  info: 'tw-text-tone-info-glyph',
  accent: 'tw-text-tone-accent-glyph',
  success: 'tw-text-tone-success-glyph',
  warning: 'tw-text-tone-warning-glyph',
  danger: 'tw-text-tone-danger-glyph',
};

const toneStyles: Record<StatusTone, string> = {
  neutral: 'tw-bg-tone-neutral-tint tw-text-tone-neutral-ink',
  info: 'tw-bg-tone-info-tint tw-text-tone-info-ink',
  accent: 'tw-bg-tone-accent-tint tw-text-tone-accent-ink',
  success: 'tw-bg-tone-success-tint tw-text-tone-success-ink',
  warning: 'tw-bg-tone-warning-tint tw-text-tone-warning-ink',
  danger: 'tw-bg-tone-danger-tint tw-text-tone-danger-ink',
};

/**
 * The deprecated domain enum, expressed as what it always was: a tone plus a default
 * label. `notGenerated` and `notPromoted` collapse onto the same tone here — they
 * produced identical styling before and differed only in copy, which is the evidence
 * that `variant` was two props wearing one name.
 */
const variantAlias: Record<StatusVariant, { tone: StatusTone; label: string }> = {
  generated: { tone: 'warning', label: 'Generated' },
  notGenerated: { tone: 'neutral', label: 'Not Generated' },
  notPromoted: { tone: 'neutral', label: 'Not Promoted' },
  generating: { tone: 'info', label: 'Generating' },
  promoting: { tone: 'accent', label: 'Promoting' },
  promoted: { tone: 'success', label: 'Promoted' },
};

/**
 * Status chip.
 *
 * **Shape reconciled with `Badge` (2026-08-24).** The two were the same primitive built
 * twice, disagreeing on radius, type token, border, height floor, gap and icon box.
 * `Badge` has live consumers outside the design system and this had none, so this one
 * moved:
 *
 * - **`rounded-ctl`, matching `TextField` / `Dropdown` / `Search`.** Decided by the
 *   design owner on 2026-08-24, overriding a change to `rounded-full` that had been made
 *   for symmetry with `Badge`. The argument against `rounded-ctl` was that it is the
 *   *control* radius, so a chip nobody can operate borrows a shape that implies it can
 *   — a real objection, and it was raised and overruled. The reason it loses: a status
 *   chip lives inside forms and table rows next to actual fields, and one radius across
 *   that surface reads as one system, where a pill reads as a foreign element floating in
 *   it. `Badge` keeps `rounded-full`; that divergence is now deliberate and is the
 *   answer to "why don't the two chips match" — see `okf/design-system/e2e.md` § 36.5.
 * - **`min-h-5` + `whitespace-nowrap`.** The height was previously incidental
 *   (`py-0.5` + a 16px line-height), so a long custom label wrapped and the chip became
 *   a two-line blob. `PromotedBadge` in ISR had already learned this and carries
 *   `whitespace-nowrap`; the design system had not. 20px is the label-chip height —
 *   `Badge`'s 28px is the interactive-chip height, and that difference is kept on
 *   purpose (see `okf/design-system/e2e.md` § 36).
 * - **Icon at 14px via a prop, not a 14px wrapper round a 20px SVG.** The wrapper could
 *   never resize an SVG that states its own width.
 *
 * Type weight stays at `sub-body-supporting` (12/500) where `Badge` is
 * `sub-title-label` (12/600), deliberately: a badge *names* a thing, a status *reports*
 * one, and the lighter weight keeps a table of chips from shouting.
 */
const Status = React.forwardRef<HTMLDivElement, StatusProps>(function Status({
  tone,
  variant,
  form = 'chip',
  label,
  timestamp,
  showAlert = false,
  alertLabel = 'Attention required',
  alertTooltip,
  pulse = false,
  live = false,
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const alias = variant ? variantAlias[variant] : undefined;
  /* An explicit `tone` wins; `variant` is only consulted as a fallback so the two can
     coexist during migration without the deprecated prop overriding the current one. */
  const resolvedTone: StatusTone = tone ?? alias?.tone ?? 'neutral';
  const displayLabel = label ?? alias?.label ?? '';

  /* `showAlert` overrides the tone rather than adding to it. That is intentional: an
     alert IS a danger state, and the label still distinguishes "Generated" from
     "Promoted". `danger` is now reachable as a tone in its own right, so red is no
     longer only expressible through a boolean on some other state. */
  const effectiveTone: StatusTone = showAlert ? 'danger' : resolvedTone;

  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  return (
    <div
      /* rest first — see the rule in Button.tsx. A caller's `role` therefore lands on
         this root and is never clobbered by the `live` role below, which goes on the
         chip. */
      {...rest}
      ref={ref}
      className={withClassName(`tw-inline-flex tw-flex-col tw-items-start`, className)}
      data-testid={dataTestId}
    >
      <div
        /* § 299 — two forms, one element. The chip is byte-identical to what it was; the
           dot form drops the tint and the pill, sets the label in body ink and paints the
           tone on a 6px disc. `data-form` says which, for specs. */
        className={
          form === 'dot'
            ? 'tw-inline-flex tw-items-center tw-gap-2 tw-min-h-5 tw-whitespace-nowrap tw-text-body-primary tw-text-ink tw-shrink-0'
            : `tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-py-0.5 tw-min-h-5 tw-rounded-ctl tw-whitespace-nowrap tw-text-sub-body-supporting tw-shrink-0 ${toneStyles[effectiveTone]}`
        }
        data-form={form}
        /* The live role goes on the CHIP, and a caller's `role` lands on the ROOT via
           the rest spread — so the two never contend. That element split is the actual
           protection; a conditional spread here would add nothing, since React omits an
           `undefined` attribute anyway. (This carried a comment claiming the spread was
           load-bearing; the test written to prove it passed against the broken version
           too, which is how the claim was caught.) */
        role={live ? 'status' : undefined}
        data-tone={effectiveTone}
        data-alert={showAlert ? 'true' : undefined}
        data-pulse={pulse ? 'true' : undefined}
        data-testid={testId('chip')}
      >
        {form === 'dot' && (
          /* § 299 — the dot. The tone's glyph ink on the wrapper, the disc in `bg-current`;
             with `pulse` the § 223 halo breathes out of this same disc, so the two forms
             share one live signal. `aria-hidden`: the label is the fact. */
          <span
            aria-hidden="true"
            className={`tw-relative tw-flex tw-h-1.5 tw-w-1.5 tw-shrink-0 ${dotInk[effectiveTone]}`}
            data-testid={testId('dot')}
          >
            {pulse && (
              <span
                className="tw-absolute tw-inset-0 tw-rounded-full tw-bg-current motion-safe:tw-animate-status-pulse"
                data-testid={testId('pulse')}
              />
            )}
            <span className="tw-relative tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-current" />
          </span>
        )}
        {form === 'chip' && pulse && (
          /*
            The live dot (§ 223). Two layers in the chip's own ink via
            `currentColor`: a static core, and a halo that starts AS the core and
            breathes out of it — so under reduced motion (animation gated off) the
            halo simply sits on the core and the whole thing is a plain dot.
            `aria-hidden`: the label already names the in-flight state; a screen
            reader must not meet a second, wordless version of it.
          */
          <span
            aria-hidden="true"
            className="tw-relative tw-flex tw-h-1.5 tw-w-1.5 tw-shrink-0"
            data-testid={testId('pulse')}
          >
            <span className="tw-absolute tw-inset-0 tw-rounded-full tw-bg-current motion-safe:tw-animate-status-pulse" />
            <span className="tw-relative tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-current" />
          </span>
        )}
        <span data-testid={testId('label')}>{displayLabel}</span>
        {showAlert && (
          <>
            {/* The triangle is decorative and says so; the sr-only text beside it is
                what actually reaches a screen reader. Before this, the alert was a red
                fill plus a wordless glyph and AT heard neither — "Generated" was read
                identically with and without it.

                `alertTooltip` adds the hover explanation (the guide-model transfer
                failure detail) and wraps ONLY when there is text to show, so a chip
                without one emits byte-identical markup to before — which is what every
                existing consumer and spec already asserts. The sr-only label stays
                outside the wrapper either way: a tooltip is pointer-only, so it can
                never be the thing assistive technology relies on. */}
            {alertTooltip ? (
              /* The DS `Tooltip`, not `Components/Common`'s `TooltipOverlay` that this
                 shipped with on release-sept-29. Two reasons, and the first is the
                 binding one: the design system may not import from `Components/Common`
                 (`dependencyDirection.test.ts`), because that is the direction that
                 turns a primitive into a leaf of the app. The second is a gain — the DS
                 tooltip clones its trigger to attach `aria-describedby`, so the
                 explanation reaches a screen reader instead of being pointer-only.
                 `usePortal` because a status chip's usual home is a table cell, and a
                 DS Table clips its popovers. */
              <Tooltip content={alertTooltip} placement="top" usePortal>
                <span
                  className="tw-inline-flex tw-items-center tw-cursor-help tw-shrink-0"
                  data-testid={testId('alert-tooltip')}
                >
                  <AlertTriangle size={14} aria-hidden="true" className="tw-shrink-0" />
                </span>
              </Tooltip>
            ) : (
              <AlertTriangle size={14} aria-hidden="true" className="tw-shrink-0" />
            )}
            <span className="tw-sr-only" data-testid={testId('alert')}>{alertLabel}</span>
          </>
        )}
      </div>

      {timestamp && (
        <Typography
          variant="caption-metadata"
          className="tw-text-ink-tertiary"
          data-testid={testId('timestamp')}
        >
          {timestamp}
        </Typography>
      )}
    </div>
  );
});

export default Status;
